import React, { useState } from 'react';
import { Mail, Smartphone } from 'lucide-react';
import { useAppointmentMutations } from '@features/admin/data';
import { showAlertModal } from '@shared/components/common/AlertModal';
import { normalizeErrorMessage } from '@/lib/errorUtils';

interface ReminderModalProps {
  appointmentId: string;
  type: 'sms' | 'email';
  onClose: () => void;
}

export const ReminderModal: React.FC<ReminderModalProps> = ({ appointmentId, type, onClose }) => {
  const appointmentMutations = useAppointmentMutations();
  const [isSending, setIsSending] = useState(false);

  const handleConfirm = async () => {
    setIsSending(true);
    try {
      await appointmentMutations.sendReminder(appointmentId, type);
      onClose();
    } catch (err: unknown) {
      showAlertModal(normalizeErrorMessage(err, 'Failed to send reminder.'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full text-center animate-scale-up border border-slate-100">
        <div
          className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${type === 'sms' ? 'bg-teal-50 text-teal-600' : 'bg-blue-50 text-blue-600'}`}
        >
          {type === 'sms' ? <Smartphone className="w-10 h-10" /> : <Mail className="w-10 h-10" />}
        </div>
        <h3 className="font-black text-xl text-slate-900 uppercase tracking-tight mb-3">
          Send Notification
        </h3>
        <p className="text-slate-500 text-sm mb-8 leading-relaxed font-medium">
          Do you want to send another reminder by{' '}
          <span className="font-black text-slate-800 uppercase">{type}</span>?
        </p>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={onClose}
            disabled={isSending}
            className="py-3 px-6 text-slate-400 font-black uppercase text-[10px] tracking-widest hover:text-slate-600 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isSending}
            className={`py-3 px-6 text-white font-black uppercase text-[10px] tracking-widest rounded-2xl shadow-lg transition-all active:scale-95 ${type === 'sms' ? 'bg-teal-600 shadow-teal-600/20' : 'bg-blue-600 shadow-blue-600/20'}`}
          >
            {isSending ? 'Sending...' : 'Confirm & Send'}
          </button>
        </div>
      </div>
    </div>
  );
};
